import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { getBiomarkersList } from '../../api/biomarkers';
import {
  ClinicalDashboardResponse,
  ClinicalDashboardRow,
  getClinicalDashboard,
} from '../../api/members';
import Button from '../../components/BaseComponents/Button';
import Loader from '../../components/BaseComponents/Loader';
import Select, { SelectOption } from '../../components/BaseComponents/Select';
import TableHeader from '../../components/BaseComponents/TableHeader';
import { formatUKDate } from '../../utils';

const PAGE_SIZE = 10;
const MIN_BIOMARKER_COLUMN_COUNT = 5;
const ALL_BIOMARKERS_OPTION: SelectOption = { key: '', value: 'All biomarkers' };

type HeaderColumn = {
  title: string;
  key: string;
  minWidth: string;
  flex: number;
};

const buildHeaderColumns = (biomarkerColumnCount: number): HeaderColumn[] => {
  const fixed: HeaderColumn[] = [
    { title: 'Member name', key: 'memberName', minWidth: '140px', flex: 1 },
    { title: 'Gender', key: 'gender', minWidth: '90px', flex: 0 },
    { title: 'DOB', key: 'dob', minWidth: '110px', flex: 0 },
    { title: 'Age', key: 'age', minWidth: '60px', flex: 0 },
    { title: 'Health concerns', key: 'healthConcerns', minWidth: '200px', flex: 1 },
    { title: 'Appointment date', key: 'appointmentDate', minWidth: '160px', flex: 0 },
  ];

  const biomarkerPairs: HeaderColumn[] = [];
  for (let idx = 0; idx < biomarkerColumnCount; idx += 1) {
    biomarkerPairs.push(
      { title: `Biomarker ${idx + 1}`, key: `bm-name-${idx}`, minWidth: '200px', flex: 1 },
      { title: `Biomarker value ${idx + 1}`, key: `bm-value-${idx}`, minWidth: '180px', flex: 0 },
    );
  }

  return [
    ...fixed,
    ...biomarkerPairs,
    { title: 'Biological age', key: 'bioAge', minWidth: '180px', flex: 1 },
  ];
};

const cellStyle = (column: HeaderColumn): React.CSSProperties => ({
  minWidth: column.minWidth,
  flex: column.flex === 0 ? '0 0 auto' : `1 0 ${column.minWidth || 'auto'}`,
  flexShrink: 0,
});

const ClinicalTab: React.FC = () => {
  const [rows, setRows] = useState<ClinicalDashboardRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState<Pick<
    ClinicalDashboardResponse,
    | 'total_members'
    | 'members_with_results'
    | 'average_biomarker_value'
    | 'numeric_biomarker_values_count'
    | 'published_biological_age_count'
    | 'unpublished_biological_age_count'
  >>({
    total_members: 0,
    members_with_results: 0,
    average_biomarker_value: null,
    numeric_biomarker_values_count: 0,
    published_biological_age_count: 0,
    unpublished_biological_age_count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [memberNameInput, setMemberNameInput] = useState('');
  const [memberNameFilter, setMemberNameFilter] = useState('');
  const [biomarkerFilter, setBiomarkerFilter] = useState('');
  const [biomarkerOptions, setBiomarkerOptions] = useState<SelectOption[]>([ALL_BIOMARKERS_OPTION]);
  const [selectedBiomarker, setSelectedBiomarker] = useState<SelectOption>(ALL_BIOMARKERS_OPTION);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setMemberNameFilter(memberNameInput.trim());
    }, 400);

    return () => {
      window.clearTimeout(handle);
    };
  }, [memberNameInput]);

  useEffect(() => {
    setOffset(0);
  }, [memberNameFilter]);

  useEffect(() => {
    const loadBiomarkers = async () => {
      try {
        const response = await getBiomarkersList(undefined, 'name', 'asc');
        const options: SelectOption[] = [
          ALL_BIOMARKERS_OPTION,
          ...response.biomarkers
            .map((biomarker) => ({
              key: biomarker.name,
              value: biomarker.name,
            }))
            .sort((a, b) => String(a.value).localeCompare(String(b.value))),
        ];
        setBiomarkerOptions(options);
      } catch (error: any) {
        toast.error(error.message || 'Failed to load biomarkers');
      }
    };

    loadBiomarkers();
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getClinicalDashboard(
        PAGE_SIZE,
        offset,
        memberNameFilter || undefined,
        biomarkerFilter || undefined,
        100
      );
      setRows(response.rows || []);
      setTotalCount(response.total_appointment_rows || 0);
      setSummary({
        total_members: response.total_members || 0,
        members_with_results: response.members_with_results || 0,
        average_biomarker_value: response.average_biomarker_value ?? null,
        numeric_biomarker_values_count: response.numeric_biomarker_values_count || 0,
        published_biological_age_count: response.published_biological_age_count || 0,
        unpublished_biological_age_count: response.unpublished_biological_age_count || 0,
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to load clinical dashboard');
    } finally {
      setLoading(false);
    }
  }, [offset, memberNameFilter, biomarkerFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleBiomarkerChange = (option: SelectOption) => {
    setSelectedBiomarker(option);
    setBiomarkerFilter(String(option.key || '').trim());
    setOffset(0);
  };

  const applyMemberSearch = () => {
    setMemberNameFilter(memberNameInput.trim());
    setOffset(0);
  };

  const handleSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyMemberSearch();
    }
  };

  const maxPage = Math.max(0, Math.ceil(totalCount / PAGE_SIZE) - 1);
  const currentPage = Math.floor(offset / PAGE_SIZE);
  const biomarkerColumnCount = Math.max(
    MIN_BIOMARKER_COLUMN_COUNT,
    ...rows.map((row) => row.biomarkers.length)
  );
  const headerColumns = useMemo(
    () => buildHeaderColumns(biomarkerColumnCount),
    [biomarkerColumnCount]
  );

  const fixedColumns = headerColumns.slice(0, 6);
  const bioAgeColumn = headerColumns[headerColumns.length - 1];
  const biomarkerColumns = headerColumns.slice(6, -1);

  return (
    <div className='clinical-dashboard-tab-panel'>
      <div className='search-row-container'>
        <div className='search-and-filter-container'>
          <div className='clinical-dashboard-filters'>
            <div className='search-input-container'>
              <input
                type='text'
                className='search-input'
                value={memberNameInput}
                onChange={(e) => setMemberNameInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder='Search by member name'
              />
            </div>
            <div className='clinical-dashboard-biomarker-select'>
              <Select
                name='biomarker'
                placeholder='Select biomarker'
                selectedOption={selectedBiomarker}
                options={biomarkerOptions}
                onChange={handleBiomarkerChange}
                isError={false}
                maxHeight='280px'
              />
            </div>
          </div>
          <div className='count-container' title='One row per member per appointment date'>
            {totalCount} {totalCount === 1 ? 'appointment row' : 'appointment rows'}
          </div>
        </div>
      </div>

      <div className='clinical-dashboard-stats'>
        <div className='stat-card'>
          <span className='stat-label'>Total members / With appointment results</span>
          <span className='stat-value'>
            {summary.total_members} / {summary.members_with_results}
          </span>
        </div>
        <div className='stat-card'>
          <span className='stat-label'>Published / Unpublished biological age</span>
          <span className='stat-value'>
            {summary.published_biological_age_count} / {summary.unpublished_biological_age_count}
          </span>
        </div>
        <div className='stat-card'>
          <span className='stat-label'>Avg biomarker value</span>
          <span className='stat-value'>
            {summary.average_biomarker_value != null
              ? `${summary.average_biomarker_value} (${summary.numeric_biomarker_values_count})`
              : 'N/A'}
          </span>
        </div>
      </div>

      <div className='clinical-dashboard-table-section'>
        <div className='scroll-container'>
          <div className='table-container clinical-dashboard-table mt-0'>
            <TableHeader headerColumns={headerColumns} gap='24px' />
            {loading ? (
              <div className='clinical-dashboard-loading'>
                <Loader height='48px' />
              </div>
            ) : rows.length === 0 ? (
              <div className='empty-members'>No rows found.</div>
            ) : (
              <div className='table-body'>
                {rows.map((row) => (
                  <div
                    key={`${row.member_id}-${row.appointment_date}`}
                    className='table-row'
                  >
                    <div className='member-name' style={cellStyle(fixedColumns[0])}>
                      {row.member_name || '-'}
                    </div>
                    <div style={cellStyle(fixedColumns[1])}>{row.gender || '-'}</div>
                    <div style={cellStyle(fixedColumns[2])}>{formatUKDate(row.dob)}</div>
                    <div style={cellStyle(fixedColumns[3])}>{row.age ?? '-'}</div>
                    <div className='health-concerns' style={cellStyle(fixedColumns[4])}>
                      {row.health_concerns.length ? row.health_concerns.join(', ') : '-'}
                    </div>
                    <div style={cellStyle(fixedColumns[5])}>
                      {formatUKDate(row.appointment_date, 'dd/MM/yyyy HH:mm')}
                    </div>
                    {Array.from({ length: biomarkerColumnCount }, (_, idx) => {
                      const nameCol = biomarkerColumns[idx * 2];
                      const valueCol = biomarkerColumns[idx * 2 + 1];
                      return (
                        <React.Fragment key={`row-${row.member_id}-${row.appointment_date}-bm-${idx}`}>
                          <div className='biomarker-name' style={cellStyle(nameCol)}>
                            {row.biomarkers[idx]?.name || '-'}
                          </div>
                          <div className='biomarker-value' style={cellStyle(valueCol)}>
                            {row.biomarkers[idx]?.value || '-'}
                          </div>
                        </React.Fragment>
                      );
                    })}
                    <div className='bio-age' style={cellStyle(bioAgeColumn)}>
                      <span
                        className={`bio-age-badge ${
                          row.biological_age_status === 'published' ? 'published' : 'not-published'
                        }`}
                      >
                        {row.biological_age_status === 'published' ? 'Published' : 'Not published'}
                      </span>
                      {row.biological_age_value != null ? ` (${row.biological_age_value})` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='clinical-dashboard-pagination'>
        <Button
          text='Previous'
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          isDisabled={currentPage === 0 || loading}
          className='btn-sm'
          width='90px'
        />
        <span className='pagination-label'>
          Page {currentPage + 1} of {maxPage + 1}
        </span>
        <Button
          text='Next'
          onClick={() => setOffset(Math.min(maxPage * PAGE_SIZE, offset + PAGE_SIZE))}
          isDisabled={currentPage >= maxPage || loading}
          width='90px'
          className='btn-sm'
        />
      </div>
    </div>
  );
};

export default ClinicalTab;
